import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FaBookOpen,
    FaBullhorn,
    FaDownload,
    FaFile,
    FaFileAlt,
    FaFileExcel,
    FaFilePdf,
    FaFilePowerpoint,
    FaFileWord,
    FaGraduationCap,
    FaLightbulb,
    FaLock,
    FaQuestionCircle,
    FaStar,
    FaTools,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../api/api";
import QuestionAndAnswer from "./QuestionAndAnswer";
import StudentsNote from "./StudentsNote";

const CLASSROOM_STYLES = `
.cayep-classroom-environment {
    width: 100%;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 16px;
    overflow: hidden;
    color: #142536;
}

.cayep-classroom-empty {
    width: 100%;
    min-height: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 16px;
    color: #142536;
}

.cayep-classroom-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 26px;
    margin-bottom: 16px;
}

.cayep-classroom-empty h3 {
    margin: 0 0 8px;
    color: #062A4B;
    font-size: 20px;
    font-weight: 800;
}

.cayep-classroom-empty p {
    margin: 0;
    color: #6B7280;
    font-size: 13px;
}

.cayep-classroom-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 22px;
    background: linear-gradient(
        135deg,
        #062A4B 0%,
        #063A6B 100%
    );
    color: #FFFFFF;
}

.cayep-classroom-header-main {
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 0;
}

.cayep-classroom-header-icon {
    width: 52px;
    height: 52px;
    min-width: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(246, 201, 68, 0.15);
    border: 1px solid rgba(246, 201, 68, 0.3);
    color: #F6C944;
    font-size: 22px;
}

.cayep-classroom-header-copy {
    min-width: 0;
}

.cayep-classroom-eyebrow {
    display: block;
    margin-bottom: 5px;
    color: #F6C944;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.2px;
}

.cayep-classroom-header-copy h2 {
    margin: 0;
    color: #FFFFFF;
    font-size: 21px;
    font-weight: 800;
    line-height: 1.25;
}

.cayep-classroom-video-context {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 12px;
}

.cayep-classroom-video-context svg {
    color: #F6C944;
}

.cayep-classroom-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 8px 11px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    font-size: 11px;
    font-weight: 700;
}

.cayep-classroom-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4FAE22;
    box-shadow: 0 0 0 3px rgba(79, 174, 34, 0.15);
}

.cayep-classroom-approval-notice {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    margin: 18px;
    padding: 15px;
    background: #FFF9E8;
    border: 1px solid #F1D985;
    border-radius: 11px;
}

.cayep-classroom-approval-icon {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #FFF1C2;
    color: #C0750D;
}

.cayep-classroom-approval-notice strong {
    display: block;
    margin-bottom: 4px;
    color: #6B4B00;
    font-size: 13px;
    font-weight: 800;
}

.cayep-classroom-approval-notice p {
    margin: 0;
    color: #806A32;
    font-size: 12px;
    line-height: 1.5;
}

.cayep-classroom-tabs {
    display: flex;
    align-items: stretch;
    gap: 2px;
    padding: 8px;
    background: #F7F9FB;
    border-bottom: 1px solid #E0E7EC;
    overflow-x: auto;
}

.cayep-classroom-tabs button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 40px;
    padding: 8px 13px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #687684;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    transition: all 0.2s ease;
}

.cayep-classroom-tabs button:hover {
    background: #EEF3F7;
    color: #063A6B;
}

.cayep-classroom-tabs button.active {
    background: #FFFFFF;
    color: #062A4B;
    box-shadow: 0 2px 8px rgba(6, 42, 75, 0.08);
}

.cayep-classroom-tab-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}

.cayep-classroom-tabs button.active
.cayep-classroom-tab-icon {
    color: #C0750D;
}

.cayep-classroom-tab-count {
    min-width: 19px;
    height: 19px;
    padding: 0 5px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 9px;
    font-weight: 800;
}

.cayep-classroom-panel {
    min-height: 400px;
    background: #FFFFFF;
}

.cayep-classroom-loading {
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #7A8793;
    font-size: 12px;
}

.cayep-classroom-spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #E5ECF1;
    border-top-color: #0F6DAB;
    border-radius: 50%;
    animation: cayep-classroom-spin 0.8s linear infinite;
}

@keyframes cayep-classroom-spin {
    to {
        transform: rotate(360deg);
    }
}

.cayep-classroom-content-section {
    padding: 25px;
}

.cayep-classroom-section-heading {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 22px;
}

.cayep-classroom-heading-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 17px;
}

.cayep-classroom-section-kicker {
    display: block;
    margin-bottom: 5px;
    color: #0F6DAB;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1px;
}

.cayep-classroom-section-heading h3 {
    margin: 0 0 5px;
    color: #062A4B;
    font-size: 20px;
    font-weight: 800;
}

.cayep-classroom-section-heading p {
    margin: 0;
    color: #6B7280;
    font-size: 12px;
    line-height: 1.5;
}

.cayep-classroom-context-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 10px;
    padding: 6px 9px;
    border-radius: 7px;
    background: #F4F7F9;
    border: 1px solid #E0E7EC;
    color: #526273;
    font-size: 10px;
    font-weight: 700;
}

.cayep-classroom-context-pill svg {
    color: #C0750D;
}

.cayep-classroom-rich-content {
    padding: 18px;
    background: #F7F9FB;
    border: 1px solid #E0E7EC;
    border-radius: 11px;
}

.cayep-classroom-rich-content p {
    margin: 0 0 12px;
    color: #374757;
    font-size: 13px;
    line-height: 1.75;
}

.cayep-classroom-rich-content p:last-child {
    margin-bottom: 0;
}

.cayep-classroom-empty-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 30px;
    text-align: center;
    background: #F7F9FB;
    border: 1px dashed #D5DEE5;
    border-radius: 12px;
}

.cayep-classroom-empty-panel > svg {
    margin-bottom: 12px;
    color: #0F6DAB;
    font-size: 27px;
}

.cayep-classroom-empty-panel h4 {
    margin: 0 0 6px;
    color: #062A4B;
    font-size: 15px;
    font-weight: 800;
}

.cayep-classroom-empty-panel p {
    max-width: 500px;
    margin: 0;
    color: #6B7280;
    font-size: 12px;
    line-height: 1.6;
}

.cayep-classroom-announcement-list,
.cayep-classroom-review-list,
.cayep-classroom-resource-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.cayep-classroom-announcement-card,
.cayep-classroom-review-card,
.cayep-classroom-resource-card {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 15px;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 11px;
}

.cayep-classroom-announcement-icon,
.cayep-classroom-review-icon,
.cayep-classroom-resource-icon {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
}

.cayep-classroom-announcement-body,
.cayep-classroom-resource-info {
    flex: 1;
    min-width: 0;
}

.cayep-classroom-announcement-meta {
    margin-bottom: 4px;
    color: #7A8793;
    font-size: 10px;
}

.cayep-classroom-announcement-body h4 {
    margin: 0 0 5px;
    color: #062A4B;
    font-size: 14px;
    font-weight: 800;
}

.cayep-classroom-announcement-body p {
    margin: 0;
    color: #596978;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
}

.cayep-classroom-review-header {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cayep-classroom-review-header strong {
    display: block;
    color: #062A4B;
    font-size: 12px;
}

.cayep-classroom-review-header span {
    display: block;
    margin-top: 2px;
    color: #7A8793;
    font-size: 10px;
}

.cayep-classroom-review-card > p {
    flex: 1;
    margin: 0;
    color: #596978;
    font-size: 12px;
    line-height: 1.65;
    white-space: pre-wrap;
}

.cayep-classroom-resource-top {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cayep-classroom-resource-top h4 {
    margin: 0;
    color: #062A4B;
    font-size: 13px;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cayep-classroom-resource-extension {
    padding: 3px 6px;
    border-radius: 5px;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 8px;
    font-weight: 800;
    flex-shrink: 0;
}

.cayep-classroom-resource-filename {
    margin: 3px 0 0;
    color: #7A8793;
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cayep-classroom-resource-description {
    margin: 5px 0 0;
    color: #596978;
    font-size: 11px;
    line-height: 1.5;
}

.cayep-classroom-resource-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 7px;
    color: #7A8793;
    font-size: 9px;
}

.cayep-resource-meta-dot {
    color: #C4C7C7;
}

.cayep-classroom-resource-download {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    border: 0;
    border-radius: 7px;
    padding: 8px 10px;
    background: #EEF3F7;
    color: #063A6B;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
}

.cayep-classroom-resource-download:hover {
    background: #DDEAF2;
}

.cayep-classroom-resource-download:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 760px) {
    .cayep-classroom-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-classroom-status {
        align-self: flex-start;
    }

    .cayep-classroom-tabs {
        scrollbar-width: thin;
    }

    .cayep-classroom-content-section {
        padding: 18px;
    }

    .cayep-classroom-resource-card {
        flex-wrap: wrap;
    }

    .cayep-classroom-resource-download {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .cayep-classroom-header {
        padding: 17px;
    }

    .cayep-classroom-header-icon {
        width: 44px;
        height: 44px;
        min-width: 44px;
    }

    .cayep-classroom-header-copy h2 {
        font-size: 18px;
    }

    .cayep-classroom-content-section {
        padding: 14px;
    }

    .cayep-classroom-section-heading {
        gap: 10px;
    }

    .cayep-classroom-heading-icon {
        width: 36px;
        height: 36px;
        min-width: 36px;
    }

    .cayep-classroom-section-heading h3 {
        font-size: 17px;
    }

    .cayep-classroom-announcement-card,
    .cayep-classroom-review-card {
        padding: 12px;
    }
}
`;

const EMPTY_CLASSROOM = {
    overview: null,
    questions: [],
    notes: null,
    announcements: [],
    reviews: [],
    resources: [],
};

const MANAGEMENT_ROLES = new Set([
    "teacher",
    "facilitator",
    "instructor",
    "admin",
    "administrator",
    "principal",
    "proprietor",
]);

const ClassRoomEnvironment = ({
    course,
    videoType = "introduction",
    videoSection = null,
}) => {
    const [activeTab, setActiveTab] =
        useState("overview");

    const [classroom, setClassroom] =
        useState(EMPTY_CLASSROOM);

    const [loading, setLoading] =
        useState(true);

    const [
        downloadingResourceId,
        setDownloadingResourceId,
    ] = useState(null);

    const [role, setRole] =
        useState("");

    const mountedRef =
        useRef(true);

    const classroomRequestRef =
        useRef(0);

    const courseId =
        course?.id;

    const isStudent =
        role === "student";

    const isManagement =
        MANAGEMENT_ROLES.has(role);

    const isSectionVideo =
        videoType === "section" &&
        Boolean(
            videoSection?.id ||
                videoSection?.section_id ||
                videoSection?.sectionId
        );

    const sectionId = useMemo(() => {
        if (!isSectionVideo) {
            return null;
        }

        return (
            videoSection?.id ||
            videoSection?.section_id ||
            videoSection?.sectionId ||
            null
        );
    }, [
        isSectionVideo,
        videoSection,
    ]);

    const learningStatus =
        useMemo(() => {
            const value =
                course?.learning_status ??
                course?.learningStatus ??
                course?.approval_status ??
                course?.approvalStatus ??
                "";

            return String(value)
                .trim()
                .toLowerCase();
        }, [course]);

    const hasExplicitLearningStatus =
        Boolean(learningStatus);

    const isApproved =
        !hasExplicitLearningStatus ||
        learningStatus === "approved" ||
        learningStatus === "active" ||
        learningStatus === "published";

    const isPending =
        Boolean(courseId) &&
        hasExplicitLearningStatus &&
        !isApproved;

    const videoContextLabel =
        useMemo(() => {
            if (!isSectionVideo) {
                return "Introduction Video";
            }

            const number =
                videoSection?.section_number ||
                videoSection?.sectionNumber ||
                "";

            const title =
                videoSection?.title ||
                videoSection?.name ||
                "Course Section";

            if (number) {
                return `Section ${number} • ${title}`;
            }

            return title;
        }, [
            isSectionVideo,
            videoSection,
        ]);

    const classroomPath =
        useMemo(() => {
            if (!courseId) {
                return "";
            }

            if (
                isSectionVideo &&
                sectionId
            ) {
                return `api/users/subjects/${courseId}/video-sections/${sectionId}/classroom/`;
            }

            return `api/users/subjects/${courseId}/classroom/`;
        }, [
            courseId,
            isSectionVideo,
            sectionId,
        ]);

    const getArray =
        useCallback(
            (value) => {
                if (
                    Array.isArray(value)
                ) {
                    return value;
                }

                if (
                    Array.isArray(
                        value?.results
                    )
                ) {
                    return value.results;
                }

                if (
                    Array.isArray(
                        value?.data
                    )
                ) {
                    return value.data;
                }

                return [];
            },
            []
        );

    const normalizeClassroomResponse =
        useCallback(
            (data) => {
                const source =
                    data?.data &&
                    typeof data.data ===
                        "object" &&
                    !Array.isArray(
                        data.data
                    )
                        ? data.data
                        : data || {};

                return {
                    overview:
                        source?.overview ||
                        source?.course_overview ||
                        source?.video_section_overview ||
                        null,

                    questions:
                        getArray(
                            source?.questions ||
                                source?.qa ||
                                source?.question_and_answers
                        ),

                    notes:
                        source?.notes ||
                        source?.note ||
                        null,

                    announcements:
                        getArray(
                            source?.announcements
                        ),

                    reviews:
                        getArray(
                            source?.reviews
                        ),

                    resources:
                        getArray(
                            source?.resources ||
                                source?.learning_resources ||
                                source?.course_resources
                        ),
                };
            },
            [getArray]
        );

    const readStoredRole =
        useCallback(() => {
            try {
                const storedUser =
                    localStorage.getItem(
                        "user"
                    );

                if (storedUser) {
                    const parsedUser =
                        JSON.parse(
                            storedUser
                        );

                    const storedRole =
                        parsedUser?.user_type ||
                        parsedUser?.role ||
                        parsedUser?.user_role ||
                        "";

                    if (
                        storedRole
                    ) {
                        return String(
                            storedRole
                        )
                            .trim()
                            .toLowerCase();
                    }
                }
            } catch (error) {
                console.warn(
                    "Unable to read stored user:",
                    error
                );
            }

            return String(
                localStorage.getItem(
                    "role"
                ) ||
                    localStorage.getItem(
                        "user_role"
                    ) ||
                    localStorage.getItem(
                        "user_type"
                    ) ||
                    ""
            )
                .trim()
                .toLowerCase();
        }, []);

    useEffect(() => {
        mountedRef.current =
            true;

        setRole(
            readStoredRole()
        );

        const refreshRole =
            () => {
                if (
                    mountedRef.current
                ) {
                    setRole(
                        readStoredRole()
                    );
                }
            };

        window.addEventListener(
            "storage",
            refreshRole
        );

        window.addEventListener(
            "user-logged-in",
            refreshRole
        );

        window.addEventListener(
            "force-logout",
            refreshRole
        );

        return () => {
            mountedRef.current =
                false;

            window.removeEventListener(
                "storage",
                refreshRole
            );

            window.removeEventListener(
                "user-logged-in",
                refreshRole
            );

            window.removeEventListener(
                "force-logout",
                refreshRole
            );
        };
    }, [readStoredRole]);

    const formatDate =
        useCallback(
            (value) => {
                if (!value) {
                    return "";
                }

                const date =
                    new Date(value);

                if (
                    Number.isNaN(
                        date.getTime()
                    )
                ) {
                    return "";
                }

                try {
                    return new Intl.DateTimeFormat(
                        "en-US",
                        {
                            dateStyle:
                                "medium",
                            timeStyle:
                                "short",
                        }
                    ).format(date);
                } catch {
                    return "";
                }
            },
            []
        );

    const formatFileSize =
        useCallback(
            (bytes) => {
                const size =
                    Number(bytes);

                if (
                    !Number.isFinite(
                        size
                    ) ||
                    size <= 0
                ) {
                    return "File";
                }

                if (size < 1024) {
                    return `${size} B`;
                }

                if (
                    size <
                    1024 * 1024
                ) {
                    return `${(
                        size / 1024
                    ).toFixed(
                        1
                    )} KB`;
                }

                if (
                    size <
                    1024 *
                        1024 *
                        1024
                ) {
                    return `${(
                        size /
                        (1024 *
                            1024)
                    ).toFixed(
                        1
                    )} MB`;
                }

                return `${(
                    size /
                    (1024 *
                        1024 *
                        1024)
                ).toFixed(
                    1
                )} GB`;
            },
            []
        );

    const getErrorMessage =
        useCallback(
            (error, fallback) => {
                const data =
                    error?.response
                        ?.data;

                if (
                    typeof data ===
                    "string"
                ) {
                    return data;
                }

                if (data?.detail) {
                    return data.detail;
                }

                if (data?.message) {
                    return data.message;
                }

                if (data?.error) {
                    return data.error;
                }

                if (data?.question) {
                    return Array.isArray(
                        data.question
                    )
                        ? data.question.join(
                              " "
                          )
                        : data.question;
                }

                if (data?.reply) {
                    return Array.isArray(
                        data.reply
                    )
                        ? data.reply.join(
                              " "
                          )
                        : data.reply;
                }

                if (data?.content) {
                    return Array.isArray(
                        data.content
                    )
                        ? data.content.join(
                              " "
                          )
                        : data.content;
                }

                return fallback;
            },
            []
        );

    const loadClassroom =
        useCallback(
            async (
                showLoader = true
            ) => {
                if (
                    !courseId ||
                    !classroomPath
                ) {
                    if (
                        mountedRef.current
                    ) {
                        setClassroom(
                            EMPTY_CLASSROOM
                        );
                        setLoading(false);
                    }

                    return;
                }

                const requestId =
                    ++classroomRequestRef.current;

                if (
                    showLoader &&
                    mountedRef.current
                ) {
                    setLoading(true);
                }

                try {
                    const response =
                        await api.get(
                            classroomPath
                        );

                    if (
                        !mountedRef.current ||
                        requestId !==
                            classroomRequestRef.current
                    ) {
                        return;
                    }

                    const normalized =
                        normalizeClassroomResponse(
                            response?.data ||
                                {}
                        );

                    setClassroom(
                        normalized
                    );
                } catch (error) {
                    if (
                        !mountedRef.current ||
                        requestId !==
                            classroomRequestRef.current
                    ) {
                        return;
                    }

                    console.error(
                        "Unable to load classroom:",
                        error
                    );

                    setClassroom(
                        EMPTY_CLASSROOM
                    );

                    const status =
                        error?.response
                            ?.status;

                    if (
                        status !== 401 &&
                        status !== 403 &&
                        status !== 404
                    ) {
                        Swal.fire({
                            icon: "error",
                            title:
                                "Unable to Load Classroom",
                            text: getErrorMessage(
                                error,
                                "The classroom information could not be loaded."
                            ),
                            confirmButtonColor:
                                "#062A4B",
                        });
                    }
                } finally {
                    if (
                        mountedRef.current &&
                        requestId ===
                            classroomRequestRef.current &&
                        showLoader
                    ) {
                        setLoading(false);
                    }
                }
            },
            [
                courseId,
                classroomPath,
                normalizeClassroomResponse,
                getErrorMessage,
            ]
        );

    useEffect(() => {
        setActiveTab(
            "overview"
        );

        setDownloadingResourceId(
            null
        );
    }, [
        courseId,
        videoType,
        sectionId,
    ]);

    useEffect(() => {
        loadClassroom(true);
    }, [loadClassroom]);

    const getFileIcon =
        useCallback(
            (resource) => {
                const extension =
                    String(
                        resource?.file_extension ||
                            resource?.extension ||
                            ""
                    ).toLowerCase();

                const contentType =
                    String(
                        resource?.content_type ||
                            resource?.contentType ||
                            ""
                    ).toLowerCase();

                if (
                    extension ===
                        ".pdf" ||
                    contentType.includes(
                        "pdf"
                    )
                ) {
                    return (
                        <FaFilePdf />
                    );
                }

                if (
                    [
                        ".doc",
                        ".docx",
                    ].includes(
                        extension
                    ) ||
                    contentType.includes(
                        "word"
                    )
                ) {
                    return (
                        <FaFileWord />
                    );
                }

                if (
                    [
                        ".xls",
                        ".xlsx",
                        ".csv",
                    ].includes(
                        extension
                    ) ||
                    contentType.includes(
                        "excel"
                    ) ||
                    contentType.includes(
                        "spreadsheet"
                    )
                ) {
                    return (
                        <FaFileExcel />
                    );
                }

                if (
                    [
                        ".ppt",
                        ".pptx",
                    ].includes(
                        extension
                    ) ||
                    contentType.includes(
                        "powerpoint"
                    ) ||
                    contentType.includes(
                        "presentation"
                    )
                ) {
                    return (
                        <FaFilePowerpoint />
                    );
                }

                if (
                    extension ===
                    ".txt"
                ) {
                    return (
                        <FaFileAlt />
                    );
                }

                return <FaFile />;
            },
            []
        );

    const getResourceDownloadUrl =
        useCallback(
            (resource) => {
                if (
                    !resource?.id ||
                    !courseId
                ) {
                    return "";
                }

                return (
                    resource?.download_url ||
                    resource?.downloadUrl ||
                    `api/users/subjects/${courseId}/resources/${resource.id}/download/`
                );
            },
            [courseId]
        );

    const downloadResource =
        useCallback(
            async (resource) => {
                const downloadUrl =
                    getResourceDownloadUrl(
                        resource
                    );

                if (
                    !downloadUrl ||
                    !resource?.id
                ) {
                    Swal.fire({
                        icon: "error",
                        title:
                            "Download Unavailable",
                        text:
                            "This learning resource does not have a valid download link.",
                        confirmButtonColor:
                            "#062A4B",
                    });

                    return;
                }

                setDownloadingResourceId(
                    resource.id
                );

                try {
                    const response =
                        await api.get(
                            downloadUrl,
                            {
                                responseType:
                                    "blob",
                            }
                        );

                    const responseType =
                        response
                            ?.headers?.[
                            "content-type"
                        ] ||
                        resource?.content_type ||
                        "application/octet-stream";

                    const blob =
                        new Blob(
                            [
                                response.data,
                            ],
                            {
                                type: responseType,
                            }
                        );

                    const blobUrl =
                        window.URL.createObjectURL(
                            blob
                        );

                    const anchor =
                        document.createElement(
                            "a"
                        );

                    const filename =
                        resource?.original_filename ||
                        resource?.originalFilename ||
                        resource?.title ||
                        "learning-resource";

                    anchor.href =
                        blobUrl;

                    anchor.download =
                        filename;

                    document.body.appendChild(
                        anchor
                    );

                    anchor.click();

                    anchor.remove();

                    window.setTimeout(
                        () => {
                            window.URL.revokeObjectURL(
                                blobUrl
                            );
                        },
                        1000
                    );
                } catch (error) {
                    console.error(
                        "Unable to download resource:",
                        error
                    );

                    Swal.fire({
                        icon: "error",
                        title:
                            "Download Failed",
                        text: getErrorMessage(
                            error,
                            "The learning resource could not be downloaded."
                        ),
                        confirmButtonColor:
                            "#062A4B",
                    });
                } finally {
                    if (
                        mountedRef.current
                    ) {
                        setDownloadingResourceId(
                            null
                        );
                    }
                }
            },
            [
                getResourceDownloadUrl,
                getErrorMessage,
            ]
        );

    const tabs = useMemo(
        () => [
            {
                key: "overview",
                icon: (
                    <FaBookOpen />
                ),
                label: "Overview",
            },
            {
                key: "qa",
                icon: (
                    <FaQuestionCircle />
                ),
                label: "Q&A",
            },
            {
                key: "notes",
                icon: (
                    <span>
                        📝
                    </span>
                ),
                label: "Notes",
            },
            {
                key: "announcements",
                icon: (
                    <FaBullhorn />
                ),
                label: "Announcements",
            },
            {
                key: "reviews",
                icon: <FaStar />,
                label: "Reviews",
            },
            {
                key: "tools",
                icon: <FaTools />,
                label: "Learning Tools",
            },
        ],
        []
    );

    if (!courseId) {
        return (
            <>
                <style>
                    {
                        CLASSROOM_STYLES
                    }
                </style>

                <div className="cayep-classroom-empty">
                    <div className="cayep-classroom-empty-icon">
                        <FaBookOpen />
                    </div>

                    <h3>
                        Classroom
                        Unavailable
                    </h3>

                    <p>
                        Select a course to
                        access the learning
                        environment.
                    </p>
                </div>
            </>
        );
    }

    return (
        <>
            <style>
                {
                    CLASSROOM_STYLES
                }
            </style>

            <div className="cayep-classroom-environment">
                <div className="cayep-classroom-header">
                    <div className="cayep-classroom-header-main">
                        <div className="cayep-classroom-header-icon">
                            <FaGraduationCap />
                        </div>

                        <div className="cayep-classroom-header-copy">
                            <span className="cayep-classroom-eyebrow">
                                {isManagement
                                    ? "FACILITATOR VIEW"
                                    : "LEARNER VIEW"}
                            </span>

                            <h2>
                                {course?.name ||
                                    course?.title ||
                                    "Course Classroom"}
                            </h2>

                            <div className="cayep-classroom-video-context">
                                <FaBookOpen />

                                <span>
                                    {
                                        videoContextLabel
                                    }
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="cayep-classroom-status">
                        <span className="cayep-classroom-status-dot" />

                        <span>
                            {isSectionVideo
                                ? "Section Classroom"
                                : "Course Classroom"}
                        </span>
                    </div>
                </div>

                {isPending && (
                    <div className="cayep-classroom-approval-notice">
                        <div className="cayep-classroom-approval-icon">
                            <FaLock />
                        </div>

                        <div>
                            <strong>
                                Course Access
                                Pending
                            </strong>

                            <p>
                                This course is
                                awaiting
                                approval before
                                classroom
                                content can be
                                accessed.
                            </p>
                        </div>
                    </div>
                )}

                {!isPending && (
                    <>
                        <div className="cayep-classroom-tabs">
                            {tabs.map(
                                (tab) => (
                                    <button
                                        key={
                                            tab.key
                                        }
                                        type="button"
                                        className={
                                            activeTab ===
                                            tab.key
                                                ? "active"
                                                : ""
                                        }
                                        onClick={() =>
                                            setActiveTab(
                                                tab.key
                                            )
                                        }
                                    >
                                        <span className="cayep-classroom-tab-icon">
                                            {
                                                tab.icon
                                            }
                                        </span>

                                        <span className="cayep-classroom-tab-label">
                                            {
                                                tab.label
                                            }
                                        </span>

                                        {tab.key ===
                                            "qa" &&
                                            classroom.questions
                                                .length >
                                                0 && (
                                                <span className="cayep-classroom-tab-count">
                                                    {
                                                        classroom
                                                            .questions
                                                            .length
                                                    }
                                                </span>
                                            )}

                                        {tab.key ===
                                            "tools" &&
                                            classroom.resources
                                                .length >
                                                0 && (
                                                <span className="cayep-classroom-tab-count">
                                                    {
                                                        classroom
                                                            .resources
                                                            .length
                                                    }
                                                </span>
                                            )}
                                    </button>
                                )
                            )}
                        </div>

                        <div className="cayep-classroom-panel">
                            {loading ? (
                                <div className="cayep-classroom-loading">
                                    <div className="cayep-classroom-spinner" />

                                    <span>
                                        Loading
                                        classroom...
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {activeTab ===
                                        "overview" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaBookOpen />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        COURSE
                                                        CONTENT
                                                    </span>

                                                    <h3>
                                                        Overview
                                                    </h3>

                                                    <p>
                                                        Learn
                                                        more about
                                                        this video
                                                        and its
                                                        training
                                                        content.
                                                    </p>
                                                </div>
                                            </div>

                                            {classroom
                                                .overview
                                                ?.content ? (
                                                <div className="cayep-classroom-rich-content">
                                                    {String(
                                                        classroom
                                                            .overview
                                                            .content
                                                    )
                                                        .split(
                                                            /\r?\n/
                                                        )
                                                        .map(
                                                            (
                                                                paragraph,
                                                                index
                                                            ) =>
                                                                paragraph.trim() ? (
                                                                    <p
                                                                        key={
                                                                            index
                                                                        }
                                                                    >
                                                                        {
                                                                            paragraph
                                                                        }
                                                                    </p>
                                                                ) : null
                                                        )}
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaLightbulb />

                                                    <h4>
                                                        No
                                                        Overview
                                                        Yet
                                                    </h4>

                                                    <p>
                                                        The
                                                        facilitator
                                                        has not
                                                        added an
                                                        overview
                                                        for this
                                                        video yet.
                                                    </p>
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {activeTab ===
                                        "qa" && (
                                        <QuestionAndAnswer
                                            course={
                                                course
                                            }
                                            classroom={
                                                classroom
                                            }
                                            setClassroom={
                                                setClassroom
                                            }
                                            isStudent={
                                                isStudent
                                            }
                                            isManagement={
                                                isManagement
                                            }
                                            videoType={
                                                videoType
                                            }
                                            videoSection={
                                                videoSection
                                            }
                                            videoContextLabel={
                                                videoContextLabel
                                            }
                                            loadClassroom={
                                                loadClassroom
                                            }
                                        />
                                    )}

                                    {activeTab ===
                                        "notes" && (
                                        <StudentsNote
                                            course={
                                                course
                                            }
                                            classroom={
                                                classroom
                                            }
                                            setClassroom={
                                                setClassroom
                                            }
                                            isStudent={
                                                isStudent
                                            }
                                            videoType={
                                                videoType
                                            }
                                            videoSection={
                                                videoSection
                                            }
                                            loadClassroom={
                                                loadClassroom
                                            }
                                        />
                                    )}

                                    {activeTab ===
                                        "announcements" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaBullhorn />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        UPDATES
                                                    </span>

                                                    <h3>
                                                        Announcements
                                                    </h3>

                                                    <p>
                                                        Important
                                                        information
                                                        from the
                                                        facilitator
                                                        for this
                                                        video.
                                                    </p>
                                                </div>
                                            </div>

                                            {classroom
                                                .announcements
                                                .length ===
                                            0 ? (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaBullhorn />

                                                    <h4>
                                                        No
                                                        Announcements
                                                    </h4>

                                                    <p>
                                                        There are
                                                        no
                                                        announcements
                                                        for this
                                                        video at
                                                        the moment.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-announcement-list">
                                                    {classroom.announcements.map(
                                                        (
                                                            announcement,
                                                            index
                                                        ) => (
                                                            <article
                                                                className="cayep-classroom-announcement-card"
                                                                key={
                                                                    announcement?.id ||
                                                                    index
                                                                }
                                                            >
                                                                <div className="cayep-classroom-announcement-icon">
                                                                    <FaBullhorn />
                                                                </div>

                                                                <div className="cayep-classroom-announcement-body">
                                                                    <div className="cayep-classroom-announcement-meta">
                                                                        <span>
                                                                            {formatDate(
                                                                                announcement?.created_at ||
                                                                                    announcement?.createdAt
                                                                            )}
                                                                        </span>
                                                                    </div>

                                                                    <h4>
                                                                        {
                                                                            announcement?.title
                                                                        }
                                                                    </h4>

                                                                    <p>
                                                                        {
                                                                            announcement?.content
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </article>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {activeTab ===
                                        "reviews" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaStar />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        FACILITATOR
                                                        FEEDBACK
                                                    </span>

                                                    <h3>
                                                        Reviews
                                                    </h3>

                                                    <p>
                                                        Guidance
                                                        and
                                                        feedback
                                                        associated
                                                        with this
                                                        video.
                                                    </p>
                                                </div>
                                            </div>

                                            {classroom
                                                .reviews
                                                .length ===
                                            0 ? (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaStar />

                                                    <h4>
                                                        No Reviews
                                                        Yet
                                                    </h4>

                                                    <p>
                                                        No
                                                        facilitator
                                                        review has
                                                        been added
                                                        for this
                                                        video yet.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-review-list">
                                                    {classroom.reviews.map(
                                                        (
                                                            review,
                                                            index
                                                        ) => (
                                                            <article
                                                                className="cayep-classroom-review-card"
                                                                key={
                                                                    review?.id ||
                                                                    index
                                                                }
                                                            >
                                                                <div className="cayep-classroom-review-header">
                                                                    <div className="cayep-classroom-review-icon">
                                                                        <FaStar />
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            Facilitator
                                                                            Review
                                                                        </strong>

                                                                        <span>
                                                                            {formatDate(
                                                                                review?.created_at ||
                                                                                    review?.createdAt
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <p>
                                                                    {
                                                                        review?.content
                                                                    }
                                                                </p>
                                                            </article>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {activeTab ===
                                        "tools" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaTools />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        COURSE
                                                        MATERIALS
                                                    </span>

                                                    <h3>
                                                        Learning
                                                        Tools
                                                    </h3>

                                                    <p>
                                                        Download
                                                        learning
                                                        materials
                                                        provided
                                                        specifically
                                                        for this
                                                        video.
                                                    </p>

                                                    <div className="cayep-classroom-context-pill">
                                                        <FaBookOpen />

                                                        <span>
                                                            {
                                                                videoContextLabel
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {classroom
                                                .resources
                                                .length ===
                                            0 ? (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaTools />

                                                    <h4>
                                                        No Learning
                                                        Resources
                                                    </h4>

                                                    <p>
                                                        {isSectionVideo
                                                            ? "No files have been uploaded for this section yet."
                                                            : "No files have been uploaded for the Introduction Video yet."}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-resource-list">
                                                    {classroom.resources.map(
                                                        (
                                                            resource,
                                                            index
                                                        ) => {
                                                            const filename =
                                                                resource?.original_filename ||
                                                                resource?.originalFilename ||
                                                                resource?.title ||
                                                                "Learning Resource";

                                                            const extension =
                                                                resource?.file_extension ||
                                                                resource?.extension ||
                                                                "";

                                                            const fileSize =
                                                                formatFileSize(
                                                                    resource?.file_size ??
                                                                        resource?.fileSize
                                                                );

                                                            const isDownloading =
                                                                String(
                                                                    downloadingResourceId
                                                                ) ===
                                                                String(
                                                                    resource?.id
                                                                );

                                                            return (
                                                                <article
                                                                    className="cayep-classroom-resource-card"
                                                                    key={
                                                                        resource?.id ||
                                                                        index
                                                                    }
                                                                >
                                                                    <div className="cayep-classroom-resource-icon">
                                                                        {getFileIcon(
                                                                            resource
                                                                        )}
                                                                    </div>

                                                                    <div className="cayep-classroom-resource-info">
                                                                        <div className="cayep-classroom-resource-top">
                                                                            <h4>
                                                                                {
                                                                                    resource?.title
                                                                                }
                                                                            </h4>

                                                                            {extension && (
                                                                                <span className="cayep-classroom-resource-extension">
                                                                                    {String(
                                                                                        extension
                                                                                    )
                                                                                        .replace(
                                                                                            ".",
                                                                                            ""
                                                                                        )
                                                                                        .toUpperCase()}
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        <p className="cayep-classroom-resource-filename">
                                                                            {
                                                                                filename
                                                                            }
                                                                        </p>

                                                                        {resource?.description && (
                                                                            <p className="cayep-classroom-resource-description">
                                                                                {
                                                                                    resource.description
                                                                                }
                                                                            </p>
                                                                        )}

                                                                        <div className="cayep-classroom-resource-meta">
                                                                            <span>
                                                                                {
                                                                                    fileSize
                                                                                }
                                                                            </span>

                                                                            {resource?.created_at && (
                                                                                <>
                                                                                    <span className="cayep-resource-meta-dot">
                                                                                        •
                                                                                    </span>

                                                                                    <span>
                                                                                        Uploaded{" "}
                                                                                        {formatDate(
                                                                                            resource.created_at
                                                                                        )}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        className="cayep-classroom-resource-download"
                                                                        onClick={() =>
                                                                            downloadResource(
                                                                                resource
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isDownloading
                                                                        }
                                                                    >
                                                                        <FaDownload />

                                                                        <span>
                                                                            {isDownloading
                                                                                ? "Downloading..."
                                                                                : "Download"}
                                                                        </span>
                                                                    </button>
                                                                </article>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default ClassRoomEnvironment;

